import type { TitlePlate } from '@nine/shared';
import { drawTitlePlateAt, plateLayout } from './index';
import { plateClock } from './motion';
import './plates.css';

/**
 * Title plates in the windows (the character card, the title list): a canvas per plate, drawn at a
 * whole number of device pixels per unit and shown at `cssScale` CSS pixels per unit, with room
 * round it for what moves past its edges. Live plates are redrawn together, twelve times a
 * second, while they are on the page; they hold still with the rest (plates/motion.ts).
 */

/** Room round a plate for falling petals and rising sparks, in units (overlapping its neighbours). */
const ROOM = 6;

interface Live {
  readonly c: HTMLCanvasElement;
  readonly g: CanvasRenderingContext2D;
  readonly plate: TitlePlate;
  readonly text: string;
  readonly k: number;
  readonly x: number;
  readonly y: number;
  last: number;
}

const live = new Set<Live>();
let ticking = false;

function draw(p: Live, t: number): void {
  p.last = t;
  p.g.setTransform(1, 0, 0, 1, 0, 0);
  p.g.clearRect(0, 0, p.c.width, p.c.height);
  drawTitlePlateAt(p.g, p.x, p.y, { plate: p.plate, text: p.text, t, scale: p.k });
}

function tick(): void {
  const t = plateClock(performance.now() / 1000);
  for (const p of live) {
    if (!p.c.isConnected) live.delete(p);
    else if (t !== p.last) draw(p, t);
  }
  ticking = live.size > 0;
  if (ticking) requestAnimationFrame(tick);
}

/** A title's plate as an element, `cssScale` CSS pixels per unit; its text is its label. */
export function plateEl(plate: TitlePlate, text: string, cssScale = 1): HTMLCanvasElement {
  const dpr = window.devicePixelRatio || 1;
  const k = Math.max(1, Math.round(cssScale * dpr));
  const c = document.createElement('canvas');
  c.className = 'title-plate';
  c.setAttribute('role', 'img');
  c.setAttribute('aria-label', text);
  const geom = plateLayout(plate, text, k);
  const g = c.getContext('2d');
  if (!geom || !g) return c;
  c.width = (geom.w + ROOM * 2) * k;
  c.height = (geom.h + ROOM * 2) * k;
  c.style.width = `${c.width / dpr}px`;
  c.style.height = `${c.height / dpr}px`;
  c.style.margin = `${-(ROOM * k) / dpr}px`;
  const p: Live = { c, g, plate, text, k, x: (geom.tx + ROOM) * k, y: (geom.frame.b + ROOM) * k, last: -1 };
  draw(p, plateClock(performance.now() / 1000));
  live.add(p);
  if (!ticking) {
    ticking = true;
    requestAnimationFrame(tick);
  }
  return c;
}
